import { useMemo } from 'react'

import { Edit3, Plus } from 'lucide-react'

import { groupByKey } from '@fortem-monorepo/js-utilities'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { PERMISSIONS } from '@/constants/permissions'
import { SummaryRow } from '@/pages/operations/pnl-attributions/components/SummaryRow'
import { usePnlContext } from '@/pages/operations/pnl-attributions/context'
import { usePnlAttributionsActions } from '@/pages/operations/pnl-attributions/pnl-attribution-actions'
import { formatNumber } from '@/utilities/format'

export interface AttibutionsSecondTabProps {
  data: PnlAttributions.AttributionSummaryRow[]
}

export const AttibutionsSecondTab: React.FC<AttibutionsSecondTabProps> = ({ data }) => {
  const groupedByAttributionType = useMemo(() => {
    return groupByKey(data, 'attribution_type')
  }, [data])

  const { params, inspectingRow, inspectingBatchId, selectedBatchState } = usePnlContext()
  const { openAdjustPosition, openAddToPosition } = usePnlAttributionsActions()

  return (
    <>
      {selectedBatchState?.state !== 'PUBLISHED' && (
        <CanAccess permissions={[PERMISSIONS.WRITE_PNL_VALIDATION]}>
          <HeaderConfigBar>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  openAdjustPosition({
                    fund_name: params.fund_name,
                    value_date: params.value_date,
                    instrument_id: inspectingRow!.reference_instrument_id,
                    unique_identifier: inspectingRow!.unique_identifier,
                    batch_id: inspectingBatchId,
                    original_pnl: inspectingRow!.net_pnl,
                  })
                }
              >
                Adjust position <Edit3 />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  openAddToPosition({
                    fund_name: params.fund_name,
                    value_date: params.value_date,
                    instrument_id: inspectingRow!.reference_instrument_id,
                    unique_identifier: inspectingRow!.unique_identifier,
                    batch_id: inspectingBatchId,
                  })
                }
              >
                Add component to position <Plus />
              </Button>
            </div>
          </HeaderConfigBar>
        </CanAccess>
      )}
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <div className={'border bg-white p-3 rounded-md flex flex-col items-center gap-px'}>
            <span className="font-mono">{formatNumber(inspectingRow!.net_pnl)}</span>
            <span className={'uppercase text-xs tracking-wide text-muted-foreground'}>Net P&L</span>
          </div>
          {Object.entries(groupedByAttributionType).map(([type, trades]) => {
            return (
              <div key={type} className={'border bg-white p-3 rounded-md flex flex-col items-center gap-px'}>
                <span>{trades.reduce((prev, curr) => prev + curr.attribution_ids.length, 0)}</span>
                <span className={'uppercase text-xs tracking-wide text-muted-foreground'}>{type}</span>
              </div>
            )
          })}
        </div>
      </HeaderConfigBar>
      <div className="relative flex flex-col flex-1">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="w-full h-full space-y-4 p-4 overflow-y-auto">
            {data.map((row) => {
              return <SummaryRow row={row} key={row.attribution_ids.join('-')} />
            })}
          </div>
        </div>
      </div>
    </>
  )
}
